import { useCustomAudiencesApi } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import useCustomAudiencesRequest from '../../hooks/useCustomAudiencesRequest';
import { isValidBrandShare } from '../../lib/brandShare';
import { hasFilterErrors, validateFilters } from '../../lib/filterValidation';
import { logError } from '../../lib/logError';
import { parseDefinitionId } from '../../lib/parseDefinitionId';
import { blockingDropped, fieldsInTree } from '../../lib/queryMapping';
import {
  selectCountIsStale,
  selectCreateBlocker,
  selectPromptIsStale,
  selectTargetsBlocker,
  selectUnconfirmedCreateBlocker
} from '../../store/customAudiencesSlice';
import useCustomAudiencesStore from '../../store/useCustomAudiencesStore';
import CreateDefinitionView from './CreateDefinition.View';

const CreateDefinition = () => {
  const ctx = useCustomAudiencesApi();
  const navigate = useNavigate();
  const request = useCustomAudiencesRequest();
  const state = useCustomAudiencesStore(
    useShallow(store => ({
      wizard: store.wizard,
      promptStale: selectPromptIsStale(store),
      countStale: selectCountIsStale(store),
      createBlocker: selectCreateBlocker(store),
      targetsBlocker: selectTargetsBlocker(store),
      unconfirmedBlocker: selectUnconfirmedCreateBlocker(store),
      targets: store.targets,
      adAccounts: store.adAccounts,
      adAccountsError: store.adAccountsError,
      tosCheckByAdAccountId: store.tosCheckByAdAccountId,
      recheckAdAccountTos: store.recheckAdAccountTos,
      setPrompt: store.setPrompt,
      submitPreview: store.submitPreview,
      acceptPreview: store.acceptPreview,
      setWizardQuery: store.setWizardQuery,
      toggleWizardField: store.toggleWizardField,
      setWizardName: store.setWizardName,
      setWizardDescription: store.setWizardDescription,
      updateWizardFilters: store.updateWizardFilters,
      setWizardBrandSharePercent: store.setWizardBrandSharePercent,
      requestCount: store.requestCount,
      goToDeliverStep: store.goToDeliverStep,
      goToDefineStep: store.goToDefineStep,
      setTargetPlatform: store.setTargetPlatform,
      toggleTargetAdAccount: store.toggleTargetAdAccount,
      toggleLookalikeRatio: store.toggleLookalikeRatio,
      setTargetValueMetric: store.setTargetValueMetric,
      loadAdAccounts: store.loadAdAccounts,
      submitCreate: store.submitCreate,
      resetWizard: store.resetWizard,
      startCloneOf: store.startCloneOf
    }))
  );
  const { resetWizard, startCloneOf, loadAdAccounts } = state;
  const { toast } = request;
  const viewerPath = `/brand/${ctx.brandId}/custom-audiences`;
  const [searchParams] = useSearchParams();
  const cloneFromRaw = searchParams.get('cloneFrom');
  const cloneFromId = parseDefinitionId(cloneFromRaw);
  const [cloneOf, setCloneOf] = useState<string | null>(null);

  /* The draft and Deliver-to selection belong to one visit and one brand.
     Entering and leaving the page both reset them, so nothing carries over from the detail page or an earlier visit. */
  useEffect(() => {
    resetWizard();
    return resetWizard;
  }, [resetWizard]);

  useEffect(() => {
    if (!ctx.brandId) return;
    loadAdAccounts(ctx).catch(error => logError('create.adAccounts', error));
  }, [ctx, loadAdAccounts]);

  /* "Clone as new" lands here with ?cloneFrom=<id>: start from that definition's rules */
  useEffect(() => {
    if (cloneFromRaw == null) return;
    if (cloneFromId == null) {
      toast('Invalid clone link', 'error');
      return;
    }
    let cancelled = false;
    startCloneOf(ctx, cloneFromId)
      .then(result => {
        if (cancelled) return;
        if (result.data) setCloneOf(result.data.name);
        else toast(`Couldn't load the audience to clone: ${result.error.message}`, 'error');
      })
      .catch(error => logError('clone.load', error));
    return () => {
      cancelled = true;
    };
  }, [ctx, cloneFromRaw, cloneFromId, startCloneOf, toast]);

  const filterErrors = useMemo(() => validateFilters(state.wizard.filters), [state.wizard.filters]);
  const checkedFields = useMemo(() => fieldsInTree(state.wizard.query), [state.wizard.query]);
  let countBlocker: string | null = null;
  if (blockingDropped(state.wizard.dropped).length > 0)
    countBlocker = 'Finish or remove the incomplete rules first';
  else if (hasFilterErrors(filterErrors)) countBlocker = 'Fix the rule errors first';
  else if (!isValidBrandShare(state.wizard.brandSharePercent))
    countBlocker = 'Fix the brand share first';

  const onSubmitPrompt = () => state.submitPreview(ctx);

  const onCreate = async (options: { createAnyway?: boolean } = {}) => {
    const outcome = await state.submitCreate(ctx, options);
    switch (outcome.kind) {
      case 'created':
        toast(
          `Custom audience created. Building its snapshot, then delivering to ${outcome.adAccountCount} ${
            outcome.adAccountCount === 1 ? 'ad account' : 'ad accounts'
          }.`,
          'success'
        );
        navigate(`${viewerPath}/${outcome.definition.id}`);
        return;
      case 'failedAfterCreate':
        toast(`Audience saved, but its first snapshot didn't start: ${outcome.message}`, 'error');
        navigate(viewerPath);
        return;
      case 'unconfirmed':
        toast(`We couldn't confirm the audience was saved: ${outcome.message}`, 'error');
        return;
      case 'invalid':
        toast(outcome.message, 'error');
        return;
      case 'failed':
        toast(`Couldn't create the audience: ${outcome.message}`, 'error');
        return;
      default:
    }
  };

  return (
    <CreateDefinitionView
      wizard={state.wizard}
      viewerPath={viewerPath}
      cloneOf={cloneOf}
      checkedFields={checkedFields}
      countBlocker={countBlocker}
      promptStale={state.promptStale}
      countStale={state.countStale}
      filterErrors={filterErrors}
      createBlocker={state.createBlocker}
      deliverBlocker={state.createBlocker ?? state.targetsBlocker ?? state.unconfirmedBlocker}
      createAnywayDisabled={state.createBlocker != null || state.targetsBlocker != null}
      confirmDiscard={
        state.wizard.step === 'deliver' ||
        state.wizard.preview.kind !== 'idle' ||
        state.wizard.query.rules.length > 0 ||
        [state.wizard.name, state.wizard.description, state.wizard.prompt].some(
          text => text.trim() !== ''
        )
      }
      targets={state.targets}
      adAccounts={state.adAccounts}
      adAccountsError={state.adAccountsError}
      tosCheckByAdAccountId={state.tosCheckByAdAccountId}
      onRecheckTos={adAccountId => {
        state
          .recheckAdAccountTos(ctx, adAccountId)
          .catch(error => logError('create.tosRecheck', error));
      }}
      onPromptChange={state.setPrompt}
      onSubmitPrompt={onSubmitPrompt}
      onAcceptPreview={state.acceptPreview}
      onQueryChange={state.setWizardQuery}
      onToggleField={state.toggleWizardField}
      onClearFilter={state.updateWizardFilters}
      onNameChange={state.setWizardName}
      onDescriptionChange={state.setWizardDescription}
      onBrandShareChange={state.setWizardBrandSharePercent}
      onCount={() => state.requestCount(ctx)}
      onNext={state.goToDeliverStep}
      onBack={state.goToDefineStep}
      onSelectPlatform={state.setTargetPlatform}
      onToggleAdAccount={state.toggleTargetAdAccount}
      onToggleRatio={state.toggleLookalikeRatio}
      onSelectValueMetric={state.setTargetValueMetric}
      onCreate={() => onCreate()}
      onCreateAnyway={() => onCreate({ createAnyway: true })}
    />
  );
};

export default CreateDefinition;
